/**
 * Space sidebar refresh
 *
 * Shared browser event helpers for expanded Space sidebar trees.
 *
 * Primary responsibilities:
 * - Emit scoped refresh requests for one Space
 * - Optionally carry a conversation patch so Chat tab / sidebar names
 *   update immediately instead of waiting on hierarchy refetch
 * - Subscribe sidebar trees to same-tab refresh requests
 *
 * Dependencies:
 * - Browser CustomEvent API
 */

import type { SpaceConversation } from "@/types/spaces"

export const SPACE_SIDEBAR_REFRESH_EVENT = "space-sidebar-refresh"

export type SpaceSidebarRefreshDetail = {
  spaceId: string
  conversation?: SpaceConversation
}

export type SpaceSidebarRefreshOptions = {
  conversation?: SpaceConversation
}

const canUseWindow = () => typeof window !== "undefined"

export const refreshSpaceSidebar = (
  spaceId: string,
  options?: SpaceSidebarRefreshOptions
) => {
  if (!spaceId.trim() || !canUseWindow()) {
    return
  }

  window.dispatchEvent(
    new CustomEvent<SpaceSidebarRefreshDetail>(SPACE_SIDEBAR_REFRESH_EVENT, {
      detail: {
        spaceId,
        conversation: options?.conversation
      }
    })
  )
}

export const subscribeToSpaceSidebarRefresh = (
  spaceId: string,
  onRefresh: (detail: SpaceSidebarRefreshDetail) => void
) => {
  if (!spaceId.trim() || !canUseWindow()) {
    return () => {}
  }

  const handleRefresh = (event: Event) => {
    const detail = (event as CustomEvent<SpaceSidebarRefreshDetail>).detail

    if (!detail?.spaceId || detail.spaceId !== spaceId) {
      return
    }

    onRefresh(detail)
  }

  window.addEventListener(SPACE_SIDEBAR_REFRESH_EVENT, handleRefresh)

  return () => {
    window.removeEventListener(SPACE_SIDEBAR_REFRESH_EVENT, handleRefresh)
  }
}
